/**
 * Answers every /api request in the browser — the demo has no backend.
 *
 * Bodies are the RAW backend JSON: ngxh6's ApiService wraps them into
 * `{type, data, pagination}` itself. Unknown endpoints answer 200 `{}` with a
 * console warning rather than an error, because ngxh6 toasts every non-2xx.
 */
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { MockRouter, MockContext, isMockResponse } from './router';
import { personaFromHeaders } from './personas';
import { ROUTES } from './handlers';

/** Board twins that are NOT a plain /api/board/<x> → /api/<x> rewrite. */
const BOARD_NATIVE = ['/api/board/dashboard'];

@Injectable()
export class DemoApiInterceptor implements HttpInterceptor {
    private router = new MockRouter(ROUTES);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const url = new URL(req.url, location.origin);
        if (url.origin !== location.origin || !url.pathname.startsWith('/api')) {
            return next.handle(req);
        }

        let path = url.pathname.replace(/\/+$/, '') || '/';
        const board = path.startsWith('/api/board/') && !BOARD_NATIVE.some(p => path.startsWith(p));
        if (board) {
            path = path.replace('/api/board/', '/api/');
        }

        const ctx: MockContext = {
            method: req.method,
            path,
            query: url.searchParams,
            body: this.parseBody(req.body),
            params: {},
            persona: personaFromHeaders(req.headers.get('X-Switch-User'), req.headers.get('Authorization')),
            switchUser: req.headers.get('X-Switch-User') || '',
            board,
        };

        let status = 200;
        let body: unknown = {};
        let headers: Record<string, string> = {};
        const found = this.router.match(req.method, path);
        if (!found) {
            console.warn('[demo] unmocked', req.method, url.pathname + url.search);
        } else {
            ctx.params = found.params;
            try {
                const out = found.route.handler(ctx);
                if (isMockResponse(out)) {
                    status = out.status ?? 200;
                    body = out.body;
                    headers = out.headers ?? {};
                } else {
                    body = out === undefined ? {} : out;
                }
            } catch (e) {
                console.error('[demo] handler error', req.method, url.pathname, e);
            }
        }

        // ngxh6 asks for text when downloading; give it what it asked for.
        if (req.responseType === 'text' && typeof body !== 'string' && !(body instanceof Blob)) {
            body = JSON.stringify(body);
        }

        const response = new HttpResponse({ status, body, url: req.url, headers: new HttpHeaders(headers) });
        // A short, varied delay so loading states appear the way they do live;
        // the assistant "thinks" for longer, like the real model call.
        const wait = path === '/api/chat/message' ? 900 + Math.floor(Math.random() * 900) : 120 + Math.floor(Math.random() * 260);
        return of(response).pipe(delay(wait));
    }

    private parseBody(body: unknown): unknown {
        if (typeof body === 'string') {
            try {
                return JSON.parse(body);
            } catch {
                return body;
            }
        }
        return body ?? null;
    }
}
